import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";

// ── Shared helpers (same pattern used across the other custom admin pages —
// EligibilityRulesManagerPage.js and customAdminMenuModule.js each keep their
// own copy of these instead of a shared util, so we follow suit here). ──────

const getBackendBaseUrl = () => {
  const configured = (window.localStorage.getItem("openimis_backend_base_url") || "").trim();
  if (configured) return configured.endsWith("/") ? configured.slice(0, -1) : configured;
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return `${window.location.protocol}//${window.location.hostname}:3000`;
  }
  return `${window.location.protocol}//${window.location.host}`;
};

const looksLikeJwt = (value) => {
  const token = String(value || "").trim();
  return /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token);
};

const deepFindToken = (node, depth = 0) => {
  if (depth > 6 || node == null) return "";

  if (typeof node === "string") {
    const trimmed = node.trim();
    if (looksLikeJwt(trimmed)) return trimmed;
    if (/^Bearer\s+/i.test(trimmed)) {
      const candidate = trimmed.replace(/^Bearer\s+/i, "").trim();
      if (looksLikeJwt(candidate)) return candidate;
    }
    return "";
  }

  if (Array.isArray(node)) {
    for (const item of node) {
      const found = deepFindToken(item, depth + 1);
      if (found) return found;
    }
    return "";
  }

  if (typeof node === "object") {
    const tokenKeys = ["token", "jwt", "accessToken", "access_token", "idToken", "id_token"];
    for (const key of tokenKeys) {
      if (key in node) {
        const found = deepFindToken(node[key], depth + 1);
        if (found) return found;
      }
    }
    for (const value of Object.values(node)) {
      const found = deepFindToken(value, depth + 1);
      if (found) return found;
    }
  }

  return "";
};

const getSharedAuthToken = () => {
  const directKeys = ["openimis_token", "token", "jwt_token", "access_token"];
  for (const key of directKeys) {
    const value = (window.localStorage.getItem(key) || "").trim();
    if (looksLikeJwt(value)) return value;
    if (/^Bearer\s+/i.test(value)) {
      const candidate = value.replace(/^Bearer\s+/i, "").trim();
      if (looksLikeJwt(candidate)) return candidate;
    }
  }

  try {
    const serialized = window.localStorage.getItem("state");
    if (!serialized) return "";
    const parsed = JSON.parse(serialized);
    return deepFindToken(parsed);
  } catch (error) {
    return "";
  }
};

const unique = (items) => Array.from(new Set(items.filter(Boolean)));

const buildApiRoots = (baseUrl) => {
  const raw = String(baseUrl || "").trim().replace(/\/$/, "");
  if (!raw) return [];

  const baseCandidates = [raw];
  if (raw.endsWith("/api")) baseCandidates.push(raw.slice(0, -4));
  if (raw.endsWith("/front")) baseCandidates.push(raw.slice(0, -6));

  try {
    const parsed = new URL(raw);
    baseCandidates.push(`${parsed.protocol}//${parsed.host}`);
  } catch (error) {
    // Ignore malformed URL and keep raw candidates only.
  }

  const uniqueBases = unique(baseCandidates.map((x) => String(x || "").replace(/\/$/, "")));
  const roots = [];
  for (const base of uniqueBases) {
    roots.push(base.endsWith("/api") ? base : `${base}/api`);
  }
  return unique(roots.map((x) => x.replace(/\/$/, "")));
};

// Escapes a value for safe embedding inside a double-quoted GraphQL string
// literal (backslashes first, then quotes).
const escapeGqlString = (value) =>
  String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');

// ── GraphQL: reuses the same `insurees` query / projection shape that
// @openimis/fe-insuree's own INSUREE_FULL_PROJECTION uses (id, uuid, chfId,
// lastName, otherNames, dob, age, gender{code,gender}, jsonExt, family{...}),
// just trimmed to what this read-only page needs and with family.jsonExt
// added as a fallback data source (INSUREE_FULL_PROJECTION already includes
// "jsonExt" on Insuree; FAMILY_FULL_PROJECTION does not, hence adding it
// here explicitly). ───────────────────────────────────────────────────────
const INSUREE_SEARCH_PROJECTION = `
  id
  uuid
  chfId
  lastName
  otherNames
  dob
  age
  jsonExt
  gender { code gender }
  family {
    id
    uuid
    jsonExt
  }
`;

const buildInsureeSearchQuery = (filterField, value) => `
  query BeneficiaryInsureeSearch {
    insurees(first: 20, ${filterField}: "${escapeGqlString(value)}") {
      edges {
        node {
          ${INSUREE_SEARCH_PROJECTION}
        }
      }
    }
  }
`;

const runGraphqlQuery = async (query) => {
  const apiRoots = unique([
    ...buildApiRoots(getBackendBaseUrl()),
    ...buildApiRoots(`${window.location.protocol}//${window.location.host}`),
  ]);

  const token = getSharedAuthToken();
  const csrfToken = window.localStorage.getItem("csrfToken");
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (csrfToken) headers["X-CSRFToken"] = csrfToken;

  let lastError = "";
  for (const root of apiRoots) {
    try {
      const res = await fetch(`${root}/graphql`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ query }),
      });
      if (!res.ok) {
        lastError = `HTTP ${res.status}`;
        continue;
      }
      const body = await res.json();
      if (Array.isArray(body?.errors) && body.errors.length) {
        lastError = body.errors.map((e) => e?.message).filter(Boolean).join("; ") || "GraphQL error";
        continue;
      }
      return body?.data || {};
    } catch (error) {
      lastError = String(error);
    }
  }
  throw new Error(lastError || "Unable to reach the server");
};

const extractInsureeNodes = (data) => {
  const edges = data?.insurees?.edges;
  if (!Array.isArray(edges)) return [];
  return edges.map((edge) => edge?.node).filter(Boolean);
};

// ── Social assessment field-label resolution: reuses the exact same
// `/api/sync/social-assessment-rules/` endpoint that SocialUiBuilderPage
// (in customAdminMenuModule.js) already calls to load the schema, so field
// ids in a beneficiary's answers can be resolved to human-readable labels.
// No benefit_plan_id filter is sent, so this returns every enabled social
// assessment rule/section across all benefit plans. ────────────────────────
const loadSocialAssessmentFieldLabels = async () => {
  const apiRoots = unique([
    ...buildApiRoots(getBackendBaseUrl()),
    ...buildApiRoots(`${window.location.protocol}//${window.location.host}`),
  ]);
  const token = getSharedAuthToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const labelsByKey = new Map();

  for (const root of apiRoots) {
    try {
      const res = await fetch(`${root}/sync/social-assessment-rules/`, {
        credentials: "include",
        headers,
      });
      if (!res.ok) continue;
      const body = await res.json();
      const rules = Array.isArray(body?.rules) ? body.rules : [];
      if (!rules.length) continue;

      rules.forEach((rule) => {
        const uiSchema = rule?.ui_schema && typeof rule.ui_schema === "object" ? rule.ui_schema : {};
        const screen = uiSchema?.screen && typeof uiSchema.screen === "object" ? uiSchema.screen : {};
        const sectionName = String(screen?.name || rule?.name || "General").trim() || "General";
        const fields = Array.isArray(uiSchema?.fields) ? uiSchema.fields : [];
        fields.forEach((field) => {
          const key = String(field?.key || "").trim();
          if (!key || labelsByKey.has(key)) return;
          labelsByKey.set(key, {
            label: String(field?.label || "").trim() || key,
            section: sectionName,
          });
        });
      });

      if (labelsByKey.size) return labelsByKey;
    } catch (error) {
      // Try next API root candidate.
    }
  }

  return labelsByKey;
};

const safeJsonParse = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
};

const formatAddress = (address) => {
  if (!address || typeof address !== "object") return null;
  if (address.same_as_current) return "Same as current address";
  const parts = [
    address.house_no,
    address.street,
    address.village_tract,
    address.district_id != null ? `District ID: ${address.district_id}` : null,
    address.region_id != null ? `Region ID: ${address.region_id}` : null,
  ].filter((part) => part !== null && part !== undefined && String(part).trim() !== "");
  return parts.length ? parts.join(", ") : null;
};

const formatAnswerValue = (value) => {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const DetailRow = ({ label, value }) => (
  <Box display="flex" style={{ padding: "6px 0", gap: 12 }}>
    <Typography style={{ width: 200, flexShrink: 0, color: "#64748B", fontWeight: 600, fontSize: 13 }}>
      {label}
    </Typography>
    <Typography style={{ fontSize: 14, color: "#0f172a" }}>{value || "-"}</Typography>
  </Box>
);

const SectionCard = ({ title, children }) => (
  <Card style={{ borderRadius: 16, marginBottom: 16 }}>
    <CardContent>
      <Typography style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>{title}</Typography>
      <Divider style={{ marginBottom: 10 }} />
      {children}
    </CardContent>
  </Card>
);

export default function BeneficiaryInsureeDetailsPage() {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searching, setSearching] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [fieldLabels, setFieldLabels] = React.useState(new Map());

  React.useEffect(() => {
    let isMounted = true;
    loadSocialAssessmentFieldLabels().then((labels) => {
      if (isMounted) setFieldLabels(labels);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const runSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setMessage("Enter a CHF ID or name to search.");
      return;
    }

    setSearching(true);
    setMessage("");
    setResults([]);
    setSelected(null);

    try {
      const [byChfId, byOtherNames, byLastName] = await Promise.all([
        runGraphqlQuery(buildInsureeSearchQuery("chfId_Icontains", term)).catch(() => ({})),
        runGraphqlQuery(buildInsureeSearchQuery("otherNames_Icontains", term)).catch(() => ({})),
        runGraphqlQuery(buildInsureeSearchQuery("lastName_Icontains", term)).catch(() => ({})),
      ]);

      const merged = new Map();
      [byChfId, byOtherNames, byLastName].forEach((data) => {
        extractInsureeNodes(data).forEach((node) => {
          const key = node.uuid || node.id;
          if (key && !merged.has(key)) merged.set(key, node);
        });
      });

      const nodes = Array.from(merged.values());
      setResults(nodes);

      if (!nodes.length) {
        setMessage(`No beneficiary found matching "${term}".`);
      } else if (nodes.length === 1) {
        setSelected(nodes[0]);
        setMessage("");
      } else {
        setMessage(`Found ${nodes.length} matching beneficiaries. Select one below.`);
      }
    } catch (error) {
      setMessage(`Search failed: ${error}`);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") runSearch();
  };

  const insureeExt = selected ? safeJsonParse(selected.jsonExt) : null;
  const familyExt = selected ? safeJsonParse(selected?.family?.jsonExt) : null;
  const ext = insureeExt || familyExt || null;

  const currentAddress = ext ? formatAddress(ext.current_address) : null;
  const permanentAddress = ext ? formatAddress(ext.permanent_address) : null;
  const referredBy = ext?.referred_by ? String(ext.referred_by) : null;
  const referredOrganization = ext?.referred_organization ? String(ext.referred_organization) : null;

  const socialAssessmentValues =
    ext?.social_ui && typeof ext.social_ui === "object" && ext.social_ui.values && typeof ext.social_ui.values === "object"
      ? ext.social_ui.values
      : null;

  const socialAssessmentEntries = socialAssessmentValues
    ? Object.entries(socialAssessmentValues).map(([key, value]) => {
        const resolved = fieldLabels.get(key);
        return {
          key,
          label: resolved?.label || key,
          section: resolved?.section || "General",
          value: formatAnswerValue(value),
        };
      })
    : [];

  const socialAssessmentBySection = socialAssessmentEntries.reduce((acc, entry) => {
    if (!acc[entry.section]) acc[entry.section] = [];
    acc[entry.section].push(entry);
    return acc;
  }, {});

  const hasAnySocialData = Boolean(ext) && (socialAssessmentEntries.length > 0 || referredBy || referredOrganization || currentAddress || permanentAddress);

  return (
    <Box>
      <Paper square style={{ backgroundColor: "#0F766E", color: "#fff", padding: "12px 16px" }}>
        <Typography variant="h6" style={{ fontWeight: 700 }}>Beneficiary Details</Typography>
      </Paper>

      <Box style={{ backgroundColor: "#F5F7FB", minHeight: "calc(100vh - 64px)", padding: 16 }}>
        <Card style={{ borderRadius: 16, marginBottom: 16 }}>
          <CardContent>
            <Typography style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Search Beneficiary</Typography>
            <Box display="flex" style={{ gap: 10, alignItems: "center" }}>
              <TextField
                placeholder="Search by CHF ID or name…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: <Search style={{ color: "#94A3B8", marginRight: 6, fontSize: 18 }} />,
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={runSearch}
                disabled={searching}
                style={{ whiteSpace: "nowrap" }}
              >
                {searching ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Search"}
              </Button>
            </Box>
            {!!message && (
              <Typography style={{ marginTop: 10, color: "#334155", fontSize: 14 }}>{message}</Typography>
            )}
          </CardContent>
        </Card>

        {results.length > 1 && (
          <Card style={{ borderRadius: 16, marginBottom: 16 }}>
            <CardContent>
              <Typography style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Matching Beneficiaries</Typography>
              {results.map((node) => (
                <Box
                  key={node.uuid || node.id}
                  onClick={() => setSelected(node)}
                  display="flex"
                  alignItems="center"
                  style={{
                    padding: "10px 8px",
                    borderBottom: "1px solid #F1F5F9",
                    cursor: "pointer",
                    gap: 10,
                    background: selected && (selected.uuid || selected.id) === (node.uuid || node.id) ? "#EFF6FF" : "transparent",
                  }}
                >
                  <Chip label={node.chfId || "-"} size="small" />
                  <Typography style={{ fontSize: 14 }}>
                    {[node.otherNames, node.lastName].filter(Boolean).join(" ") || "-"}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {selected && (
          <>
            <SectionCard title="Demographics">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DetailRow label="CHF ID" value={selected.chfId} />
                  <DetailRow label="Name" value={[selected.otherNames, selected.lastName].filter(Boolean).join(" ")} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailRow label="Date of Birth" value={selected.dob} />
                  <DetailRow label="Gender" value={selected?.gender?.gender || selected?.gender?.code} />
                </Grid>
              </Grid>
            </SectionCard>

            {!hasAnySocialData ? (
              <SectionCard title="Social Assessment">
                <Typography style={{ color: "#94A3B8", fontSize: 14 }}>
                  No social assessment data available for this beneficiary.
                </Typography>
              </SectionCard>
            ) : (
              <>
                <SectionCard title="Address">
                  <DetailRow label="Current Address" value={currentAddress} />
                  <DetailRow label="Permanent Address" value={permanentAddress} />
                </SectionCard>

                <SectionCard title="Referral">
                  <DetailRow label="Referred By" value={referredBy} />
                  <DetailRow label="Referred Organization" value={referredOrganization} />
                </SectionCard>

                <SectionCard title="Social Assessment Answers">
                  {!socialAssessmentEntries.length ? (
                    <Typography style={{ color: "#94A3B8", fontSize: 14 }}>
                      No social assessment data available for this beneficiary.
                    </Typography>
                  ) : (
                    Object.entries(socialAssessmentBySection).map(([section, entries]) => (
                      <Box key={section} style={{ marginBottom: 14 }}>
                        <Typography style={{ fontSize: 14, fontWeight: 700, color: "#0F766E", marginBottom: 6 }}>
                          {section}
                        </Typography>
                        {entries.map((entry) => (
                          <DetailRow key={entry.key} label={entry.label} value={entry.value} />
                        ))}
                      </Box>
                    ))
                  )}
                </SectionCard>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
